import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { formatAxiosError } from '../../context/AuthContext';
import { useSiteConfig } from '../../context/SiteConfigContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { LOGO_FULL_FALLBACK } from './constants';
import { RegisterIdVerificationStep } from './RegisterIdVerificationStep';
import { RegisterMerchantFields } from './RegisterMerchantFields';
import { AddressFieldsAutosuggest } from './AddressFieldsAutosuggest';
import { PhoneInput } from '../PhoneInput';
import { BirthdatePicker } from './BirthdatePicker';
import { resolveRefCode, resolveMroCode, clearStoredReferral } from '../../utils/referralTracker';
import { useFunnelTracker, usePageViewEvent } from '../../utils/funnelTracker';

export function RegisterPage() {
  const { login } = useAuth();
  const { logoUrl } = useSiteConfig();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { type } = useParams();
  const LOGO_FULL = logoUrl || LOGO_FULL_FALLBACK;
  const isMerchant = type === 'merchant';
  // Funnel analytics — anonymous drop-off tracking on /register.
  const fireFunnel = useFunnelTracker(isMerchant ? 'merchant' : 'member');
  usePageViewEvent(fireFunnel, 'register_page_view');
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    mobile: '',
    referral_code: resolveRefCode(searchParams.get('ref')),
    birthdate: '',
    business_name: '',
    industry_id: '',
    business_type: '',
    business_address: '',
    business_description: '',
  });
  const [address, setAddress] = useState({
    address_line: '',
    city: '',
    state: '',
    country: '',
    country_code: '',
    state_id: null,
    city_id: null,
    postal_code: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [idFile, setIdFile] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [permitFile, setPermitFile] = useState(null);
  const [permitPreview, setPermitPreview] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleStep1 = (e) => {
    e.preventDefault();
    setError('');
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return; }
    if (formData.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (!formData.birthdate) { setError('Birthdate is required'); return; }

    const birth = new Date(formData.birthdate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    if (today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age--;
    if (age < 18) { setError('You must be at least 18 years old to register'); return; }
    if (!acceptedTerms) { setError('You must accept the Terms of Service and Privacy Policy'); return; }
    // Mobile is required for both subscribers and merchants
    if (!formData.mobile || formData.mobile.replace(/\D/g, '').length < 7) {
      setError('Please enter a valid mobile number with the correct country code');
      return;
    }
    if (isMerchant && !formData.business_name.trim()) { setError('Business name is required for merchant registration'); return; }
    if (isMerchant && !formData.industry_id) { setError('Please select your business industry'); return; }
    // Address validation
    if (!address.address_line.trim()) { setError('Street address is required'); return; }
    if (!address.country || !address.country_code) { setError('Please select your country'); return; }
    if (!address.state.trim()) { setError('Please select your state / province'); return; }
    if (!address.city.trim()) { setError('Please select your city'); return; }

    setStep(2);
    fireFunnel('step1_complete');
  };

  // Compress images client-side before upload to make slow-network uploads feasible.
  // Resizes long edge to 1600px, re-encodes as JPEG @ 0.85 quality.
  const compressImage = (file) => new Promise((resolve) => {
    if (!file || !file.type?.startsWith('image/')) { resolve(file); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const maxSide = 1600;
        let { width, height } = img;
        if (width > maxSide || height > maxSide) {
          if (width > height) { height = (height * maxSide) / width; width = maxSide; }
          else { width = (width * maxSide) / height; height = maxSide; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (!blob) { resolve(file); return; }
          const newName = file.name.replace(/\.[^.]+$/, '') + '.jpg';
          resolve(new File([blob], newName, { type: 'image/jpeg' }));
        }, 'image/jpeg', 0.85);
      };
      img.onerror = () => resolve(file);
      img.src = e.target.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });

  const uploadFile = async (file, purpose = 'id-photo', applicantName = '') => {
    const compressed = await compressImage(file);
    const fd = new FormData();
    fd.append('file', compressed);
    fd.append('purpose', purpose);
    if (applicantName) fd.append('name', applicantName);
    // 60s timeout for slow cellular networks
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/registration`, {
        method: 'POST', body: fd, signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`Upload failed (${res.status}): ${body.slice(0, 120)}`);
      }
      const data = await res.json();
      return data.url || data.file_url || '';
    } catch (e) {
      clearTimeout(timeoutId);
      if (e.name === 'AbortError') {
        throw new Error('Photo upload timed out. Your connection may be too slow — try a faster network or smaller photo.');
      }
      throw e;
    }
  };

  const handleRegister = async () => {
    if (!idFile || !selfieFile) { setError('Please upload both your ID and selfie'); return; }
    setError('');
    setLoading(true);

    try {
      const applicantName = formData.name?.trim() || '';
      const merchantName = isMerchant ? (formData.business_name?.trim() || applicantName) : applicantName;
      setStatusMsg('Uploading your ID photo...');
      const idUrl = await uploadFile(idFile, 'id-photo', applicantName);
      setStatusMsg('Uploading your selfie...');
      const selfieUrl = await uploadFile(selfieFile, 'selfie', applicantName);

      if (!idUrl || !selfieUrl) {
        setError('Failed to upload images. Please try again.');
        setLoading(false);
        setStatusMsg('');
        return;
      }

      const permitUrl = isMerchant && permitFile
        ? (setStatusMsg('Uploading business permit...'), await uploadFile(permitFile, 'business-permit', merchantName))
        : '';

      // Step A: Pre-verify ID via OCR. The /verify-id endpoint now returns an
      // attempt_id immediately and runs OCR in the background — so the request
      // can't hit Cloudflare's ~100s timeout. We poll /verify-id-status/{id}
      // every 3 s until OCR completes.
      setStatusMsg('Verifying your ID...');
      let verificationToken = null;
      try {
        const preVerifyResp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-id`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            id_photo_url: idUrl,
            selfie_url: selfieUrl,
            mobile: formData.mobile,
            birthdate: formData.birthdate,
            is_merchant: isMerchant,
            business_name: isMerchant ? formData.business_name : null,
            industry_id: isMerchant ? (formData.industry_id || null) : null,
            business_address: isMerchant ? (formData.business_address || null) : null,
            business_description: isMerchant ? (formData.business_description || null) : null,
            business_permit_url: isMerchant ? (permitUrl || null) : null,
            referral_code: formData.referral_code || null,
            mro_code: isMerchant ? (resolveMroCode(searchParams.get('mro')) || null) : null,
            password: formData.password,
          }),
        });
        if (!preVerifyResp.ok) {
          const body = await preVerifyResp.json().catch(() => ({}));
          const detail = body?.detail || `Verification failed (${preVerifyResp.status})`;
          throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
        }
        const startData = await preVerifyResp.json();
        const attemptId = startData.attempt_id;
        if (!attemptId) throw new Error('Verification did not start. Please try again.');

        // Poll status with 3s interval and 3-minute cap (60 polls × 3s = 180s).
        const MAX_POLLS = 60;
        const POLL_INTERVAL_MS = 3000;
        let lastStatus = null;
        for (let i = 0; i < MAX_POLLS; i++) {
          await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
          const statusResp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-id-status/${attemptId}`);
          if (!statusResp.ok) {
            throw new Error(`Could not check verification status (${statusResp.status}). Please retry.`);
          }
          lastStatus = await statusResp.json();
          if (lastStatus.state === 'verified') {
            verificationToken = lastStatus.verification_token;
            break;
          }
          if (lastStatus.state === 'failed') {
            throw new Error(lastStatus.error || 'ID verification failed.');
          }
          setStatusMsg(`Verifying your ID... (${(i + 1) * 3}s)`);
        }
        if (!verificationToken) {
          throw new Error('ID verification timed out. Please try again in a few minutes.');
        }
      } catch (e) {
        setError(e.message || 'ID verification failed. Please try again.');
        setLoading(false);
        setStatusMsg('');
        return;
      }

      // Step B: Final registration — now a fast (~1s) call, no OCR.
      setStatusMsg('Creating your account...');
      const registerPayload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        mobile: formData.mobile,
        referral_code: formData.referral_code || null,
        birthdate: formData.birthdate,
        id_photo_url: idUrl,
        selfie_url: selfieUrl,
        is_merchant: isMerchant,
        mro_code: isMerchant ? (resolveMroCode(searchParams.get('mro')) || null) : null,
        id_verification_token: verificationToken,
        address_line: address.address_line,
        city: address.city,
        state: address.state,
        country: address.country,
        country_code: address.country_code,
        state_id: address.state_id || null,
        city_id: address.city_id || null,
        postal_code: address.postal_code || null,
      };

      if (isMerchant) {
        registerPayload.business_name = formData.business_name;
        registerPayload.industry_id = formData.industry_id || null;
        registerPayload.business_type = formData.business_type || null;
        registerPayload.business_address = formData.business_address || null;
        registerPayload.business_description = formData.business_description || null;
        registerPayload.business_permit_url = permitUrl || null;
      }

      fireFunnel('register_submit_attempt');
      const result = await login(null, null, {
        isRegister: true,
        registerData: registerPayload,
      });

      if (result?.success !== false) {
        fireFunnel('register_submit_success');
        // Wipe stored referral cookies so a shared device doesn't auto-fill
        // unrelated future signups.
        clearStoredReferral();
        navigate('/dashboard');
      } else {
        setError(result?.error || 'Registration failed');
      }
    } catch (err) {
      setError(formatAxiosError(err, 'Registration failed'));
    } finally {
      setLoading(false);
      setStatusMsg('');
    }
  };

  if (step === 2) {
    return (
      <RegisterIdVerificationStep
        isMerchant={isMerchant}
        error={error}
        statusMsg={statusMsg}
        loading={loading}
        idFile={idFile}
        idPreview={idPreview}
        setIdFile={setIdFile}
        setIdPreview={setIdPreview}
        selfieFile={selfieFile}
        selfiePreview={selfiePreview}
        setSelfieFile={setSelfieFile}
        setSelfiePreview={setSelfiePreview}
        permitFile={permitFile}
        permitPreview={permitPreview}
        setPermitFile={setPermitFile}
        setPermitPreview={setPermitPreview}
        onBack={() => setStep(1)}
        onSubmit={handleRegister}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#000B18] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_FULL} alt="All Global Access" className="h-16 mx-auto mb-6" />
          </Link>
          <h1 className="font-outfit text-3xl font-bold text-white mb-2">Create your free account</h1>
          <p className="text-[#A0B4CC]" data-testid="register-free-subtitle">Membership is free — browse every partner deal. Upgrade to Pro or Elite anytime to get the discounts.</p>
        </div>

        <div className="flex mb-6 rounded-xl overflow-hidden border border-white/10 bg-[#041A3B]" data-testid="register-tabs">
          <Link
            to="/register"
            className={`flex-1 py-3.5 text-center text-sm font-medium transition-all ${
              !isMerchant
                ? 'bg-[#00E5FF]/10 text-[#00E5FF] border-b-2 border-[#00E5FF]'
                : 'text-[#5C7599] hover:text-[#A0B4CC]'
            }`}
            data-testid="tab-member"
          >
            Member
          </Link>
          <Link
            to="/register/merchant"
            className={`flex-1 py-3.5 text-center text-sm font-medium transition-all ${
              isMerchant
                ? 'bg-[#00E676]/10 text-[#00E676] border-b-2 border-[#00E676]'
                : 'text-[#5C7599] hover:text-[#A0B4CC]'
            }`}
            data-testid="tab-merchant"
          >
            Merchant
          </Link>
        </div>

        {isMerchant && (
          <div className="mb-4 p-3 rounded-lg bg-[#00E676]/5 border border-[#00E676]/20 text-center">
            <p className="text-[#00E676] text-sm font-outfit font-semibold">Promote your business for FREE - Forever!</p>
            <p className="text-[#A0B4CC] text-xs mt-0.5">Register as a merchant and list your services on AGA</p>
          </div>
        )}

        <Card className="bg-[#041A3B] border-white/10">
          <CardContent className="p-6">
            <form onSubmit={handleStep1} className="space-y-4">
              {error && (
                <div className="p-3 rounded-md bg-[#FF1744]/10 border border-[#FF1744]/30 text-[#FF1744] text-sm" data-testid="register-error">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="name" className="text-white">Full Name *</Label>
                <Input id="name" name="name" placeholder="John Doe" value={formData.name} onChange={handleChange} required
                  onFocus={() => fireFunnel('field_focus:name')}
                  className="bg-[#000B18] border-white/20 text-white placeholder:text-[#5C7599] focus:border-[#00E5FF]" data-testid="register-name-input" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-white">Email *</Label>
                <Input id="email" name="email" type="email" placeholder="you@example.com" value={formData.email} onChange={handleChange} required
                  onFocus={() => fireFunnel('field_focus:email')}
                  className="bg-[#000B18] border-white/20 text-white placeholder:text-[#5C7599] focus:border-[#00E5FF]" data-testid="register-email-input" />
              </div>

              <div className="space-y-2" onFocus={() => fireFunnel('field_focus:birthdate')}>
                <Label htmlFor="birthdate" className="text-white">Birthdate * <span className="text-[#5C7599] text-xs">(Must be 18+)</span></Label>
                <BirthdatePicker
                  value={formData.birthdate}
                  onChange={(iso) => setFormData(prev => ({ ...prev, birthdate: iso }))}
                  required
                  minAgeYears={18}
                  testIdPrefix="register-birthdate"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mobile" className="text-white">Mobile *</Label>
                <PhoneInput
                  value={formData.mobile}
                  onChange={(v) => setFormData((f) => ({ ...f, mobile: v }))}
                  required
                  data-testid="register-mobile-phone-input"
                />
                <p className="text-[#5C7599] text-xs">Choose your country code and enter your number.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-white">Password *</Label>
                <div className="relative">
                  <Input id="password" name="password" type={showPassword ? 'text' : 'password'} placeholder="Min 6 characters" value={formData.password} onChange={handleChange} required
                    className="bg-[#000B18] border-white/20 text-white placeholder:text-[#5C7599] focus:border-[#00E5FF] pr-10" data-testid="register-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C7599] hover:text-white">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-white">Confirm Password *</Label>
                <Input id="confirmPassword" name="confirmPassword" type="password" placeholder="Re-enter password" value={formData.confirmPassword} onChange={handleChange} required
                  className="bg-[#000B18] border-white/20 text-white placeholder:text-[#5C7599] focus:border-[#00E5FF]" data-testid="register-confirm-password-input" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="referral_code" className="text-white">Referral Code (Optional)</Label>
                <Input id="referral_code" name="referral_code" placeholder="AGAXXXXXX" value={formData.referral_code} onChange={handleChange}
                  className="bg-[#000B18] border-white/20 text-white placeholder:text-[#5C7599] focus:border-[#00E5FF]" data-testid="register-referral-input" />
              </div>

              {isMerchant && <RegisterMerchantFields formData={formData} handleChange={handleChange} />}

              <div className="pt-2 border-t border-white/10">
                <AddressFieldsAutosuggest value={address} onChange={setAddress} required />
              </div>

              <div className="flex items-start gap-3 pt-1">
                <input
                  type="checkbox"
                  id="acceptTerms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-white/20 bg-[#000B18] accent-[#00E5FF] cursor-pointer flex-shrink-0"
                  data-testid="register-accept-terms"
                />
                <label htmlFor="acceptTerms" className="text-[#A0B4CC] text-sm leading-relaxed cursor-pointer">
                  I agree to the{' '}
                  <Link to="/page/terms-of-service" target="_blank" className="text-[#00E5FF] hover:underline" data-testid="terms-link">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/page/privacy-policy" target="_blank" className="text-[#00E5FF] hover:underline" data-testid="privacy-link">Privacy Policy</Link>
                </label>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <div className="h-1 flex-1 rounded-full bg-[#00E5FF]"></div>
                <div className="h-1 flex-1 rounded-full bg-white/10"></div>
                <span className="text-[#5C7599] text-xs">Step 1 of 2</span>
              </div>

              <Button type="submit" className="w-full btn-primary h-11" data-testid="register-next-btn">
                Next: Upload ID & Selfie <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-[#A0B4CC] text-sm">
                Already have an account?{' '}
                <Link to="/login" className="text-[#00E5FF] hover:underline">Sign in</Link>
              </p>
            </div>
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-[#5C7599] text-xs">
          By creating an account, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}
